import { ReactNode, createContext, useContext, useEffect, useRef } from 'react';

import { UseMutationResult } from '@tanstack/react-query';
import styled from 'styled-components';

import { IconModeDark } from '@components/icons/icon-mode-dark';
import { IconModeLight } from '@components/icons/icon-mode-light';
import { IconTheme } from '@components/icons/icon-theme';
import { Nav } from '@components/nav';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { ToggleGroup } from '@components/toggle-group';
import { useKeyboardSecret } from '@hooks/use-keyboard-secret';
import { useScopedTranslate } from '@locale/translate';
import { disableTransitions, enableTransitions, noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { useLocalStorageState } from './component-helpers';

export type ThemeApps = 'journeys' | 'hire';
export type ThemeVariant = 'light' | 'dark';

type ThemeContextType = {
  currentTheme: ThemeVariant;
  setTheme(newTheme: ThemeVariant): void;
};

const ThemeContext = createContext<ThemeContextType>({
  currentTheme: 'light',
  setTheme: noop,
});

export const useCurrentTheme = () => {
  return useContext(ThemeContext);
};

export const useThemeSyncer = (currentTheme: ThemeVariant) => {
  const initialRender = useRef(true);

  useEffect(() => {
    const updateDOM = () => {
      if (currentTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    // Skip animation on initial page load
    if (initialRender.current) {
      initialRender.current = false;
      updateDOM();
      return;
    }

    // Use View Transitions API for animated theme switch
    if (document.startViewTransition) {
      const transition = document.startViewTransition(() => {
        disableTransitions();
        updateDOM();
      });
      transition.finished.then(enableTransitions);
    } else {
      // Fallback for browsers without View Transitions API
      disableTransitions();
      updateDOM();
      const timeout = setTimeout(enableTransitions, 500);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [currentTheme]);
};

export const useLocalStorageTheme = (app: ThemeApps) => {
  const [storedTheme, setStoredTheme] = useLocalStorageState<ThemeVariant>({
    defaultValue: 'light',
    key: `${app}-theme`,
  });

  return { storedTheme, setStoredTheme };
};

const useTheme = ({
  theme,
  updateTheme,
  app,
}: {
  theme?: ThemeVariant;
  updateTheme?: UseMutationResult<unknown, unknown, FormData>;
  app: ThemeApps;
}) => {
  const { storedTheme, setStoredTheme } = useLocalStorageTheme(app);
  const currentTheme = theme ?? storedTheme;
  const setTheme = async (theme: ThemeVariant) => {
    const formData = new FormData();
    formData.append('theme', theme);
    if (updateTheme) {
      await updateTheme.mutateAsync(formData, {
        onSuccess: () => {
          setStoredTheme(theme);
        },
      });
    } else {
      setStoredTheme(theme);
    }
  };

  useThemeSyncer(currentTheme);
  return { currentTheme, setTheme };
};
export const ThemeProvider = ({
  children,
  app,
  theme,
  updateTheme,
}: {
  children: ReactNode;
  app: ThemeApps;
  theme?: ThemeVariant;
  updateTheme?: () => UseMutationResult<unknown, unknown, FormData>;
}) => {
  const updateMutation = updateTheme?.();
  const { currentTheme, setTheme } = useTheme({ app, theme, updateTheme: updateMutation });

  useKeyboardSecret({
    shortcut: 'iddqd',
    onSuccess: () => {
      setTheme(currentTheme === 'light' ? 'dark' : 'light');
    },
  });

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        setTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeToggle = () => {
  const { t } = useScopedTranslate(locale);
  const { currentTheme, setTheme } = useCurrentTheme();

  return (
    <Nav.MenuItem
      variant="muted"
      icon={<IconTheme />}
      label={t('theme')}
      iconRight={
        <ToggleGroup.Root defaultValue={currentTheme} onChange={setTheme}>
          <ToggleGroup.IconButton icon={IconModeLight} value="light" />
          <ToggleGroup.IconButton icon={IconModeDark} value="dark" />
        </ToggleGroup.Root>
      }
    />
  );
};

export const StandaloneThemeToggle = () => {
  const { t } = useScopedTranslate(locale);
  const { currentTheme, setTheme } = useCurrentTheme();

  return (
    <Container>
      <IconTheme color="elementPurple80" />
      <Label>
        <Stack space={1} orientation="horizontal" align="center">
          {t('interface')}
          <Text color="textHelper" text="•" />
          {t('theme')}
        </Stack>
      </Label>
      <ToggleGroup.Root defaultValue={currentTheme} onChange={setTheme}>
        <ToggleGroup.IconButton icon={IconModeLight} value="light" />
        <ToggleGroup.IconButton icon={IconModeDark} value="dark" />
      </ToggleGroup.Root>
    </Container>
  );
};

const Container = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
  align: 'center',
  justify: 'space-between',
})({
  width: '100%',
});

const Label = styled.div({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  justifyContent: 'flex-start',
  flexGrow: 1,
  fontSize: pxToRem(14),
});

const locale = {
  en: {
    theme: 'Theme',
    interface: 'Interface',
  },
};
